<template>
  <div
    class="messages-container"
    :class="{ 'has-giveaway-banner': hasGiveawaysBanner }"
  >
    <PinnedMessages
      @delete="openDeleteModal"
      @mute="openMuteModal"
      @block="openBlockModal"
      @unpin="openUnpinModal"
      :editable="true"
    />

    <div class="messages scrollable" ref="messages">
      <ModeratorMessage
        v-for="(message, index) in messages"
        :key="message.id"
        :user="message.user"
        :message="message"
        :isLastMessageFromSameUser="isLastMessageFromSameUser(message, index)"
        :isPrevMessageUserSame="isPrevMessageUserSame(message, index)"
        @delete="openDeleteModal(message)"
        @mute="openMuteModal(message.user)"
        @block="openBlockModal(message.user)"
      />
    </div>

    <div class="chat-errors" v-if="chatErrors.length">
      <NotificationBanner
        v-for="chatError in chatErrors"
        :key="chatError.message"
      >
        {{ chatError.message }}
      </NotificationBanner>
    </div>

    <!-- Admin message input -->
    <form class="message-input" ref="formRef" @submit.prevent="sendMessage">
      <MentionInput
        ref="mentionInput"
        :disabled="!user.id"
        v-model="text"
        :asyncSearch="searchUsers"
        @submit="() => $refs.formRef.requestSubmit()"
      />

      <Button
        v-show="!isScrolled"
        @click="() => scrollToBottom(true)"
        class="show-last"
        type="primary"
        style="width: 20px; height: 20px"
      >
        <Arrow />
      </Button>

      <div class="input-actions">
        <SmileIcon
          ref="emojiToggle"
          style="cursor: pointer"
          :disable-skin-tone="true"
          @click="toggleEmojiPicker"
        />

        <EmojiPicker
          v-show="showEmojiPicker"
          v-if="emojiToggleEl"
          v-click-outside="{
            handler: closeEmojiPicker,
            exclude: emojiToggleEl,
          }"
          style="position: absolute; bottom: 40px; left: 0"
          :native="true"
          @select="onSelectEmoji"
          theme="dark"
        />

        <Button action="submit" type="empty" style="width: 20px; height: 20px">
          <SendMessage />
        </Button>
      </div>
    </form>

    <!-- Delete modal -->
    <Modal
      v-if="showDeleteModal"
      @close="closeActionModal"
      img="Modal-background.png"
      name="Delete message"
    >
      <div class="admin-modal">
        <p>Delete this message?</p>
        <div class="actions">
          <Button type="primary__white" size="small" @click="closeActionModal">
            Cancel
          </Button>
          <Button type="primary" size="small" @click="confirmDeleteMessage">
            Delete
          </Button>
        </div>
      </div>
    </Modal>

    <!-- Mute modal -->
    <Modal
      v-if="showMuteModal"
      @close="closeActionModal"
      img="Modal-background.png"
      name="Mute user"
    >
      <div class="admin-modal">
        <p>Mute {{ selectedUser?.name }} for:</p>
        <div>
          <input
            v-model="muteDuration"
            type="number"
            min="60"
            step="60"
            placeholder="Duration in seconds"
          />
          seconds
        </div>
        <div class="actions">
          <Button type="primary__white" size="small" @click="closeActionModal">
            Cancel
          </Button>
          <Button type="primary" size="small" @click="confirmMuteUser">
            Mute
          </Button>
        </div>
      </div>
    </Modal>

    <!-- Block modal -->
    <BlockUserModal
      v-if="showBlockModal && selectedUser"
      :user="selectedUser"
      mode="moderator"
      @close="closeActionModal"
    />

    <Modal
      v-if="showUnpinModal"
      @close="closeActionModal"
      img="Modal-background.png"
      name="Unpin message"
    >
      <div class="admin-modal">
        <p>Are you sure you want to unpin this message?</p>
        <div class="actions">
          <Button type="primary__white" size="small" @click="closeActionModal">
            Cancel
          </Button>
          <Button type="primary" size="small" @click="confirmUnpinMessage">
            Unpin
          </Button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent } from "vue"
import chatMixin from "@/components/chat/mixins/chat"
import Modal from "@/components/uikit/Modal.vue"
import Button from "@/components/uikit/Button.vue"
import ModeratorMessage from "./ModeratorMessage.vue"
import PinnedMessages from "../PinnedMessages.vue"
import { MessageType, UserType } from "../../types"
import { callApi } from "@/services/api"
import BlockUserModal from "../Admin/BlockUserModal.vue"

const EmojiPicker = defineAsyncComponent(() => import("vue3-emoji-picker"))

export default defineComponent({
  name: "ModeratorGlobalChat",
  mixins: [chatMixin],
  components: {
    EmojiPicker,
    Modal,
    Button,
    ModeratorMessage,
    PinnedMessages,
    BlockUserModal,
  },

  data() {
    return {
      showDeleteModal: false,
      showMuteModal: false,
      showBlockModal: false,
      showUnpinModal: false,

      siteNotificationTitle: "",
      siteNotificationMessage: "",

      selectedMessage: null as null | MessageType,
      selectedUser: null as null | UserType,

      muteDuration: 300,
    }
  },

  computed: {
    pinnedMessages() {
      return this.$store.getters["global/pinnedMessages"]
    },
  },

  methods: {
    openDeleteModal(message: MessageType) {
      this.selectedMessage = message
      this.showDeleteModal = true
    },

    openMuteModal(user: UserType) {
      this.selectedUser = user
      this.showMuteModal = true
    },

    openBlockModal(user: UserType) {
      this.selectedUser = user
      this.showBlockModal = true
    },
    openUnpinModal(message: MessageType) {
      this.selectedMessage = message
      this.showUnpinModal = true
    },

    closeActionModal() {
      this.selectedMessage = null
      this.siteNotificationMessage = ""
      this.siteNotificationTitle = ""
      this.selectedUser = null
      this.showDeleteModal = false
      this.showMuteModal = false
      this.showBlockModal = false
      this.showUnpinModal = false
    },

    async sendSiteNotification() {
      if (
        !this.siteNotificationTitle.trim() ||
        !this.siteNotificationMessage.trim()
      )
        return

      this.$privateSocket.emit("admin:siteNotification", {
        title: this.siteNotificationTitle.trim(),
        message: this.siteNotificationMessage.trim(),
      })

      this.siteNotificationTitle = ""
      this.siteNotificationMessage = ""
    },

    async confirmDeleteMessage() {
      if (!this.selectedMessage) return

      this.$privateSocket.emit("admin:deleteChatMessage", {
        messageId: this.selectedMessage.id,
      })

      this.closeActionModal()
    },

    async confirmMuteUser() {
      if (!this.selectedUser) return

      this.$privateSocket.emit("admin:muteUserChat", {
        userId: this.selectedUser.id,
        duration: this.muteDuration,
      })

      this.closeActionModal()
    },

    async confirmBlockUser({
      reason,
      duration,
    }: {
      reason: string
      duration: number
    }) {
      if (!this.selectedUser) return

      if (!reason) {
        return
      }

      callApi.moderator.blockUserChat(this.selectedUser.id, {
        seconds: duration,
        reason,
      })

      this.closeActionModal()
    },
    async confirmPinMessage() {
      if (!this.selectedMessage) return

      this.$privateSocket.emit("admin:pinChatMessage", {
        messageId: this.selectedMessage.id,
      })

      this.closeActionModal()
    },

    async confirmUnpinMessage() {
      if (!this.selectedMessage) return

      this.$privateSocket.emit("admin:unpinChatMessage", {
        message: this.selectedMessage,
      })

      this.closeActionModal()
    },
  },
})
</script>

<style lang="scss" scoped>
.messages-container {
  display: flex;
  flex-direction: column;
  height: calc(100% - var(--header-height));
  flex: 1;

  &.has-giveaway-banner {
    max-height: calc(100% - var(--header-height) - 228px);
  }
}

.admin-toolbar {
  display: flex;
  gap: 10px;
  padding: 8px 8px 0 8px;
  flex-wrap: wrap;
  justify-content: end;
}

.messages {
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 8px;
  flex-grow: 1;

  & > :first-child {
    margin-top: auto;
  }
}

.chat-errors {
  margin: 0 8px;
}

.message-input {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  box-sizing: border-box;
  padding: 12px 16px;
  margin: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  div {
    display: flex;
    gap: 8px;
    i {
      cursor: pointer;
    }
  }
}

.input-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  position: relative;
}

.show-last {
  position: absolute !important;
  top: -50px;
  right: 10px;
  padding: 15px !important;

  svg {
    transform: rotate(180deg);
  }
}

// @media screen and (max-width: 950px) {
//   .messages-container {
//     height: calc(100% - var(--header-height) - 83px) !important;

//     &.has-giveaway-banner {
//       max-height: calc(100% - 48px - 32px - 223px - 64px - 4px) !important;
//     }
//   }
// }
</style>
